import {basicContext} from "../../utils/contextUtils";
import {decrypt} from "../../utils/crypto";
import {setFieldVisibility} from "../../../apps/KpModule/actions";
import {fieldPathJoinGetter} from "../../utils/kp3Utils";
import {activityNames, establishmentStatusObjects} from "./staticEntities";
import async from "async";
import Errors from "../../utils/Errors";
import {generateFetchFieldListAction} from "../../../apps/KpModule/actions/api";
import {relabelOtherActivityOption} from "./utils/otherActivity";

const _ = require('lodash')

export const entities = [
    {
        name: "ShelterImage",
        type: "mongoInternal",
        fields: [
            {path: 'name', notEmpty: true},
            'User',
            {path: 'date', notEmpty: true}
        ]
    },
    {
        name: 'Shelter',
        facets: [{name: 'codeName', codeTKey: 'siret_spec'}, 'conversations', 'files'],
        fields: [
            {path: 'types', type: 'ShelterType', link: 'MTM', nullable: true},
            {path: 'animalTypes', type: 'AnimalType', link: 'MTM', nullable: true},
            'address',
            'additionalAddress',
            'shelterSchedules',
            'zipCode',
            'city',
            //{path: 'zone', type: 'Zone'},
            'country',
            {path: 'lat', type: 'decimal', nullable: true},
            {path: 'lng', type: 'decimal', nullable: true},
            //'Organization',
            { path: 'createdAt', type: 'date', nullable: true },
            { path: 'createdBy', type: 'User', nullable: true },

            { path: 'lastModificationDate', type: 'date', nullable: true },
            { path: 'lastUser', type: 'User', nullable: true },
            {
                path: 'users',
                $f: (shelter, context, callback) => {
                    global.app.C.CUser.find({
                        ...basicContext(context),
                        fieldPath: ['id', 'civility.id', 'civility.name', 'firstname', 'lastname', 'mail', 'phone', 'functions.id', 'functions.name'],
                        query: {shelters: {$elemMatch : {$eq: new global.ObjectID(shelter.id)}}}
                    }, (e, users) => {
                        callback(
                            e,
                            users.map(user => ({
                                ...user,
                                civility: context.tc(user.civility?.name),
                                mail: decrypt(user.mail),
                                phone: decrypt(user.phone),
                                active: user.active,
                                functions: user.functions.map(userFunction => userFunction.name).join(', ')
                            }))
                        )
                    })
                }
            },
            {path: 'status', type: 'EstablishmentStatus'},
            {path: 'publishOnWebsite', type: 'boolean'},
            'website',
            'facebookLink',
            'instagramLink',
            'tiktokLink',
            'youtubeLink',
            'linkedinLink',
            'syntheticPresentation',
            'teamAndMissions',
            'keyFigures',
            {path: 'images', type: 'ShelterImage', link: 'OTM'},
            {
                path: 'colleagueFiles',
                type: 'ColleagueFile',
                link: "OTM",
                fieldPath: ['files.id', 'files.filename', 'files.date', 'files.user.id', 'files.user.name', 'organization.users.kpUser.id'],
                $f: function(shelter, context, callback) {
                    const colleagueKpUserIds = new Set(
                        (shelter.organization?.users || []).map(u => u.kpUser?.id).filter(Boolean)
                    )
                    return callback(null, (shelter.files || []).filter(
                        file => file.user && colleagueKpUserIds.has(file.user.id)
                    ))
                },
                $s: function(newColleagueFiles, shelter, context, callback) {
                    const colleagueKpUserIds = new Set(
                        (shelter.organization?.users || []).map(u => u.kpUser?.id).filter(Boolean)
                    )
                    const nonColleagueFiles = (shelter.files || []).filter(
                        f => !f.user || !colleagueKpUserIds.has(f.user.id)
                    )
                    shelter.files = [...nonColleagueFiles, ...(newColleagueFiles || [])]
                    delete shelter.colleagueFiles
                    callback(null, newColleagueFiles)
                }
            },
            {path: 'establishmentContact', type: 'CUser', link: { oppositeField: { path: 'handledShelters' } }, nullable: true},
            fieldPathJoinGetter({path: "siretName", fieldPath: ["code", "name"]}),
            {
                path: 'typesLabel',
                fieldPath: ['types.id', 'types.name', 'organization.otherActivity'],
                $f: (shelter, context, callback) => {
                    return callback(null, activityNames(shelter.types, shelter.organization?.otherActivity).join(', '))
                }
            },
            {
                path: "siretNameStatus",
                fieldPath: ["code", "name", "status.name"],
                f: function() {
                    return `${_.get(this, "code")} - ${_.get(this, "name")} (${_.get(this, "status.name")})`
                }
            },
        ],
        filters: [
            {
                name: 'byOrganization',
                isDefault: false,
                query: (context) => {
                    const organizationId = _.get(context, 'data.organizationId')
                    return organizationId
                        ? {organization: new global.ObjectID(organizationId)}
                        : {_id: null}
                }
            },
            {
                name: 'byOrganizationAndNotClosed',
                isDefault: false,
                query: (context) => {
                    const organizationId = _.get(context, 'data.organizationId')
                    return organizationId ? {
                        organization: new global.ObjectID(organizationId),
                        status: {$ne: 'closed'}
                    } : {_id: null}
                }
            },
            {
                name: "byUserOrganization",
                isDefault: false,
                async: true,
                query: function(context, callback) {
                    const user = context.user
                    global.app.C.CUser.get({kpUser: new global.ObjectID(user.id)}, {
                        ...basicContext(context),
                        fieldPath: ['id', 'organization.id', 'organization.organizationType.id', 'organization.status.id']
                    }, (error, cUser) => {
                        if(error) return callback(error)
                        const userOrgIsMemberAssociation = cUser?.organization?.organizationType?.id === 'association' //&& cUser.organization?.status?.id === 'member'
                        if(!userOrgIsMemberAssociation) return callback(null, {_id: null})
                        callback(null, {organization: new global.ObjectID(cUser.organization.id)})
                    })
                }
            },
            {
                name: "byUser",
                isDefault: false,
                async: true,
                query: function(context, callback) {
                    const user = context.user
                    global.app.C.CUser.get({kpUser: new global.ObjectID(user.id)}, {
                        ...basicContext(context),
                        fieldPath: ['id', 'organization.id', 'organization.organizationType.id', 'organization.status.id', 'shelters.id']
                    }, (error, cUser) => {
                        if(error) return callback(error)
                        const userOrgIsMemberAssociation = cUser?.organization?.organizationType?.id === 'association' //&& cUser.organization?.status?.id === 'member'
                        if(!userOrgIsMemberAssociation) return callback(null, {_id: null})
                        callback(
                            null,
                            !!cUser.shelters.length
                                ? {_id: {$in: cUser.shelters.map(shelter => new global.ObjectID(shelter.id))}}
                                : {organization: new global.ObjectID(cUser.organization.id)}
                        )
                    })
                }
            },
        ],
        validateSave: async function(newObject, oldObject, context, callback) {
            if (context.clientContext.moduleId === 'm-C-myShelter') return callback()
            if(newObject.status.id !== 'headquarters') return callback()

            const newOrganizations = await global.app.C.Shelter.find({
                ...basicContext(context),
                fieldPath: [],
                query: {
                    _id: {$ne: new global.ObjectID(newObject.id)},
                    status: 'headquarters',
                    organization: new global.ObjectID(newObject.organization.id)
                }
            })

            if(!!newOrganizations.length) return callback(new Errors.ValidationError("Le statut siège ne peut être affecté qu'à un seul établissement"))
            return callback()
        },
        beforeSave: (object, oldObject, context, callback) => {

            if (context.restAction && context.restAction.crudType === "C") {
                object.createdAt = new Date()
                object.createdBy = new global.ObjectID(context.user.id)
            }

            object.lastUser = new global.ObjectID(context.user.id)
            object.lastModificationDate = new Date()
            callback(null, object, oldObject)
        }
    }
]

export const module_ = {
    object: 'Shelter',
    tKey: 'mTitle_shelter',
    objectIdentifier: 'name',
    category: {
        path: 'menuTitle_phoneBook',
        icon: 'notebook'
    },
    formSections: [
        {
            id: 'presentation',
            tKey: "Présentation",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'socialMedia',
            tKey: "Site et réseaux sociaux",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'animal',
            tKey: "Activités animalières",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'address',
            tKey: "Coordonnées et horaires",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'documents',
            tKey: "Documents",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'chat',
            tKey: "Historique d'échanges",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        }
    ],
    viewMap: {
        dt: [
            {path: 'organization.organizationType.name', tKey: 'type organisation', initiallyNotVisible: true},
            {path: 'organization', tKey: 'Organisation', display: 'sirenTradingName'},
            {path: 'code', tKey: 'siret_spec'},
            {path: 'status', tKey: 'status'},
            {path: 'name'},
            {path: 'publishOnWebsite', tKey: 'publier sur le site'},
            {path: 'typesLabel', tKey: 'activities_spec', initiallyNotVisible: true},
            {path: 'animalTypes', tKey: 'species', display: 'name', initiallyNotVisible: true},
            {path: 'address', tKey: 'address_spec', initiallyNotVisible: true},
            {path: 'additionalAddress', tKey: 'additionalAddress_spec', initiallyNotVisible: true},
            {path: 'zipCode', tKey: 'zipCode_spec'},
            {path: 'city', tKey: 'city_spec', initiallyNotVisible: true},
            {path: 'lastUser', initiallyNotVisible: true},
            {path: 'lastModificationDate', initiallyNotVisible: true},
        ],
        form: {
            fields: [
                {
                    path: 'organization',
                    tKey: 'Organisation',
                    editable: false,
                    display: 'sirenTradingName',
                    //filters: ['isAssociation'],
                    fieldPath: ['id', 'name', 'code', 'tradingName', 'sirenTradingName', 'animalTypes.id', 'activities.id', 'otherActivity'],
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-shelter.Shelter_animalTypes",
                                    store.getState,
                                    {
                                        data: {animalTypes: newValue?.animalTypes}
                                    }
                                )
                            )
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-shelter.Shelter_types",
                                    store.getState,
                                    {
                                        data: {activitiesTypes: newValue?.activities}
                                    }
                                )
                            )
                                .then(() => relabelOtherActivityOption(store, "m-C-shelter.Shelter_types", newValue?.otherActivity))
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-shelter.Shelter_establishmentContact",
                                    store.getState,
                                    {
                                        data: {organizationId: newValue?.id}
                                    }
                                )
                            )
                        }
                    }
                },
                {path: 'code', widthClassName: "col-xs-12 col-sm-6", tKey: 'siret_spec', required: true, placeholder: 'Renseigner le SIRET', maxLength: 14, noSpaces: true, numerical: true},
                {
                    path: 'status',
                    widthClassName: "col-xs-12 col-sm-6",
                    tKey: 'status',
                    default: establishmentStatusObjects.find(obj => obj.id === 'headquarters'),
                    placeholder: 'Sélectionner le statut'
                },
                {path: 'name', required: true, placeholder: 'Renseigner le nom'},
                {path: 'publishOnWebsite'},
                {
                    path: 'establishmentContact',
                    tKey: 'Contact à afficher sur le site « defensedelanimal.fr »',
                    display: 'completeInfo',
                    fieldPath: ['id', 'completeInfo'],
                    filters: ['byOrganizationId'],
                },

                {path: 'syntheticPresentation', tKey: 'Présentation synthétique', type: 'textarea', section: 'presentation'},
                {path: 'teamAndMissions', tKey: 'Équipe et missions', type: 'textarea', section: 'presentation'},
                {path: 'keyFigures', tKey: 'Chiffres clés', type: 'textarea', section: 'presentation'},
                {
                    path: 'images',
                    tKey: "Photos de l'établissement",
                    type: 'image',
                    fieldPath: ['id', 'name', 'type', 'user.id', 'user.name', 'date'],
                    section: 'presentation'
                },

                {path: 'website', tKey: 'Site internet', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
                {path: 'facebookLink', tKey: 'Facebook', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
                {path: 'instagramLink', tKey: 'Instagram', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
                {path: 'tiktokLink', tKey: 'TikTok', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
                {path: 'youtubeLink', tKey: 'YouTube', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
                {path: 'linkedinLink', tKey: 'LinkedIn', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},

                {
                    path: 'types',
                    section: 'animal',
                    tKey: 'activities_spec',
                    placeholder: 'Sélectionner les activités',
                    sortList: true,
                    filters: ['byOrganizations']
                },
                {path: 'animalTypes', section: 'animal', tKey: 'species', placeholder: 'Sélectionner les espèces', display: 'name', filters: ['byOrganizations']},

                {path: 'address', section: 'address', tKey: 'address_spec', placeholder: "Renseigner l'adresse", required: true},
                {path: 'additionalAddress', section: 'address', tKey: 'additionalAddress_spec', placeholder: "Renseigner le complément d'adresse"},
                {path: 'zipCode', section: 'address', widthClassName: "col-xs-12 col-sm-6", tKey: 'zipCode_spec', placeholder: 'Renseigner le code postal', maxLength: 5, noSpaces: true, required: true, numerical: true},
                {path: 'city', section: 'address', widthClassName: "col-xs-12 col-sm-6", tKey: 'city_spec', placeholder: 'Renseigner la ville', required: true},
                {path: 'lat', section: 'address', widthClassName: "col-xs-12 col-sm-6", tKey: 'lat_spec', placeholder: 'Renseigner la latitude'},
                {path: 'lng', section: 'address', widthClassName: "col-xs-12 col-sm-6", tKey: 'lng_spec', placeholder: 'Renseigner la longitude'},
                {path: 'shelterSchedules', section: 'address', type: 'textarea'},
                {
                    path: 'users',
                    section: 'address',
                    tKey: 'contacts',
                    type: 'accordion',
                    disabled: true,
                    viewMap: {
                        dt: [
                            {path: 'civility'},
                            {path: 'firstname'},
                            {path: 'lastname'},
                            {path: 'mail'},
                            {path: 'phone'},
                            {path: 'functions'},
                            {path: 'active', type: 'boolean', width: 100}
                        ]
                    }
                },
                {path: 'country', tKey: 'country_spec', default: 'France', placeholder: 'Renseigner le pays', hidden: true, disabled: true, required: true},

                {path: 'files', type: 'simpleFiles', section: 'documents', tKey: "documents liés à l'établissement"},
                {path: 'conversations', section: 'chat', tKey: "interactionZone"}
            ],
            onOpen: ({ store, module }) => {
                const state = store.getState()
                const objectMode = state.ui.objectMode

                const usersField = module.viewMap.form.fields.find(field => field.path === 'users')

                store.dispatch(setFieldVisibility(usersField.id, objectMode !== 'newObject'))
            }
        }
    }
}

export const userModule_ = {
    name: 'MyShelter',
    object: 'Shelter',
    tKey: 'mTitle_myShelter',
    newable: false,
    removable: false,
    objectIdentifier: 'name',
    category: {
        path: 'menuTitle_myAssociation',
        icon: 'building'
    },
    formSections: [
        {
            id: 'presentation',
            tKey: "Présentation",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'socialMedia',
            tKey: "Site et réseaux sociaux",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'animal',
            tKey: "Activités animalières",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'address',
            tKey: "Coordonnées et horaires",
            defaultCollapsed: false,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'documents',
            tKey: "Documents",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        }
    ],
    viewMap: {
        dt: [
            {path: 'code', tKey: 'siret_spec'},
            {path: 'status', tKey: 'status'},
            'name',
            {path: 'publishOnWebsite', tKey: 'publier sur le site'},
            {path: 'typesLabel', tKey: 'activities_spec', initiallyNotVisible: true},
            {path: 'animalTypes', tKey: 'species', display: 'name', initiallyNotVisible: true},
            {path: 'address', initiallyNotVisible: true},
            {path: 'additionalAddress', initiallyNotVisible: true},
            'zipCode',
            {path: 'city', tKey: 'city_spec', initiallyNotVisible: true},
        ],
        form: [
            {
                path: 'organization',
                tKey: 'Association',
                display: 'fullName',
                filters: ['isAssociation'],
                editable: false,
                fieldPath: ['id', 'name', 'code', 'fullName', 'animalTypes.id', 'activities.id', 'otherActivity'],
                subscriptions: {
                    onChange: (newValue, oldValue, {store, module}) => {
                        store.dispatch(
                            generateFetchFieldListAction(
                                "m-C-myShelter.Shelter_animalTypes",
                                store.getState,
                                {
                                    data: {animalTypes: newValue?.animalTypes}
                                }
                            )
                        )
                        store.dispatch(
                            generateFetchFieldListAction(
                                "m-C-myShelter.Shelter_types",
                                store.getState,
                                {
                                    data: {activitiesTypes: newValue?.activities}
                                }
                            )
                        )
                            .then(() => relabelOtherActivityOption(store, "m-C-myShelter.Shelter_types", newValue?.otherActivity))
                    }
                }
            },
            {path: 'code', tKey: 'siret_spec', disabled: true, widthClassName: "col-xs-12 col-sm-6",},
            {path: 'status', tKey: 'status', disabled: true, widthClassName: "col-xs-12 col-sm-6",},
            {path: 'name', required: true, placeholder: 'Renseigner le nom'},
            {path: 'publishOnWebsite', type: 'checkbox'},
            {
                path: 'establishmentContact',
                tKey: 'Contact à afficher sur le site « defensedelanimal.fr »',
                display: 'completeInfo',
                fieldPath: ['id', 'completeInfo'],
                filters: ['byOrganization'],
            },

            {path: 'syntheticPresentation', tKey: 'Présentation synthétique', type: 'textarea', section: 'presentation'},
            {path: 'teamAndMissions', tKey: 'Équipe et missions', type: 'textarea', section: 'presentation'},
            {path: 'keyFigures', tKey: 'Chiffres clés', type: 'textarea', section: 'presentation'},
            {
                path: 'images',
                tKey: "Photos de l'établissement",
                type: 'image',
                fieldPath: ['id', 'name', 'type', 'user.id', 'user.name', 'date'],
                section: 'presentation'
            },

            {path: 'website', tKey: 'Site internet', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
            {path: 'facebookLink', tKey: 'Facebook', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
            {path: 'instagramLink', tKey: 'Instagram', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
            {path: 'tiktokLink', tKey: 'TikTok', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
            {path: 'youtubeLink', tKey: 'YouTube', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},
            {path: 'linkedinLink', tKey: 'LinkedIn', widthClassName: 'col-xs-12 col-sm-6', section: 'socialMedia'},

            {
                path: 'types',
                tKey: 'activities_spec',
                placeholder: 'Sélectionner les activités',
                sortList: true,
                section: 'animal',
                filters: ['byOrganizations']
            },
            {path: 'animalTypes', tKey: 'species', placeholder: 'Sélectionner les espèces', display: 'name', filters: ['byOrganizations'], section: 'animal'},

            {path: 'address', disabled: true, section: 'address'},
            {path: 'additionalAddress', disabled: true, section: 'address'},
            {path: 'zipCode', disabled: true, section: 'address', widthClassName: "col-xs-12 col-sm-6"},
            {path: 'city', disabled: true, section: 'address', widthClassName: "col-xs-12 col-sm-6"},
            {path: 'lat', disabled: true, section: 'address', widthClassName: "col-xs-12 col-sm-6"},
            {path: 'lng', disabled: true, section: 'address', widthClassName: "col-xs-12 col-sm-6"},
            {path: 'country', tKey: 'country_spec', default: 'France', placeholder: 'Renseigner le pays', hidden: true, disabled: true, required: true},

            {path: 'shelterSchedules', type: 'textarea', section: 'address'},
            {
                path: 'users',
                section: 'address',
                tKey: 'contacts',
                type: 'accordion',
                disabled: true,
                viewMap: {
                    dt: [
                        {path: 'civility'},
                        {path: 'firstname'},
                        {path: 'lastname'},
                        {path: 'mail'},
                        {path: 'phone'},
                        {path: 'functions'},
                        {path: 'active', type: 'boolean', width: 100}
                    ]
                }
            },

            {path: 'colleagueFiles', type: 'simpleFiles', section: 'documents', tKey: "documents liés à l'établissement"},
            {path: 'files', hidden: true}
        ]
    },
    filters: [
        'byUser'
    ]
}
